import { cleanupRefBinding } from './ref-bindings';
export function createDOMRangeManager(context) {
    const { nodeMap, nodeTypes, eventHandlers, childMap, parentMap, nodeProps, nodeThemes, nodeDataKeys, refBindings, inputStates, } = context;
    const isFragment = (id) => nodeTypes.get(id) === 'Fragment';
    const getContainerForParent = (parentId) => {
        const parent = nodeMap.get(parentId);
        if (!parent) {
            return null;
        }
        if (!isFragment(parentId)) {
            return parent;
        }
        return parent.parentNode;
    };
    const getTerminalNodeForParent = (parentId) => {
        if (!isFragment(parentId)) {
            return null;
        }
        return nodeMap.get(parentId) ?? null;
    };
    const getFirstRenderedNode = (id) => {
        if (!isFragment(id)) {
            return nodeMap.get(id) ?? null;
        }
        const children = childMap.get(id) ?? [];
        for (const childId of children) {
            const firstChildNode = getFirstRenderedNode(childId);
            if (firstChildNode) {
                return firstChildNode;
            }
        }
        return nodeMap.get(id) ?? null;
    };
    const getFirstAttachedRenderedNode = (id, parent) => {
        if (!isFragment(id)) {
            const node = nodeMap.get(id);
            return node?.parentNode === parent ? node : null;
        }
        const children = childMap.get(id) ?? [];
        for (const childId of children) {
            const childNode = getFirstAttachedRenderedNode(childId, parent);
            if (childNode) {
                return childNode;
            }
        }
        const anchor = nodeMap.get(id);
        return anchor?.parentNode === parent ? anchor : null;
    };
    const getLastRenderedNode = (id) => {
        if (isFragment(id)) {
            return nodeMap.get(id) ?? null;
        }
        return nodeMap.get(id) ?? null;
    };
    const collectLogicalRange = (id) => {
        if (!isFragment(id)) {
            const node = nodeMap.get(id);
            return node ? [node] : [];
        }
        const nodes = [];
        const children = childMap.get(id) ?? [];
        for (const childId of children) {
            nodes.push(...collectLogicalRange(childId));
        }
        const anchor = nodeMap.get(id);
        if (anchor) {
            nodes.push(anchor);
        }
        return nodes;
    };
    const collectRenderedRange = (id) => {
        if (isFragment(id)) {
            return collectLogicalRange(id);
        }
        const first = getFirstRenderedNode(id);
        const last = getLastRenderedNode(id);
        if (!first || !last) {
            return [];
        }
        if (first.parentNode && first.parentNode === last.parentNode) {
            const nodes = [];
            let current = first;
            while (current) {
                nodes.push(current);
                if (current === last) {
                    return nodes;
                }
                current = current.nextSibling;
            }
        }
        return collectLogicalRange(id);
    };
    const getNodeAfterRange = (id) => {
        const last = getLastRenderedNode(id);
        if (!last) {
            return null;
        }
        return last.nextSibling;
    };
    const detachRenderedRange = (id) => {
        const nodes = collectRenderedRange(id);
        if (nodes.length === 0) {
            return;
        }
        for (const node of nodes) {
            const parent = node.parentNode;
            if (parent) {
                parent.removeChild(node);
            }
        }
    };
    const insertRenderedRange = (id, parent, beforeNode, document) => {
        const nodes = collectRenderedRange(id);
        if (nodes.length === 0) {
            return;
        }
        if (beforeNode && nodes.includes(beforeNode)) {
            return;
        }
        const fragment = document.createDocumentFragment();
        for (const node of nodes) {
            fragment.appendChild(node);
        }
        parent.insertBefore(fragment, beforeNode);
    };
    const cleanupNode = (id) => {
        nodeMap.delete(id);
        nodeTypes.delete(id);
        const handlers = eventHandlers.get(id);
        if (handlers) {
            handlers.clear();
            eventHandlers.delete(id);
        }
        childMap.delete(id);
        parentMap.delete(id);
        nodeProps.delete(id);
        nodeThemes.delete(id);
        nodeDataKeys.delete(id);
        inputStates.delete(id);
        cleanupRefBinding(id, refBindings);
    };
    const cleanupSubtree = (id) => {
        const children = childMap.get(id) ?? [];
        for (const childId of children) {
            cleanupSubtree(childId);
        }
        cleanupNode(id);
    };
    return {
        getContainerForParent,
        getTerminalNodeForParent,
        getFirstRenderedNode,
        getFirstAttachedRenderedNode,
        getLastRenderedNode,
        getNodeAfterRange,
        detachRenderedRange,
        insertRenderedRange,
        cleanupNode,
        cleanupSubtree,
    };
}
//# sourceMappingURL=ranges.js.map